import React, { useMemo, useState } from "react";
import type { PreviewTab } from "../../data/landingData";

function SectionHeading({
    eyebrow,
    title,
    subtitle,
}: {
    eyebrow?: string;
    title: string;
    subtitle?: string;
}) {
    return (
        <div className="mx-auto mb-10 max-w-3xl text-center">
            {eyebrow ? (
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
                    <span className="h-2 w-2 rounded-full bg-white/60" />
                    {eyebrow}
                </div>
            ) : null}
            <h2 className="text-balance text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                {title}
            </h2>
            {subtitle ? (
                <p className="mt-3 text-pretty text-sm text-white/70 sm:text-base">{subtitle}</p>
            ) : null}
        </div>
    );
}

export default function Preview({ tabs }: { tabs: PreviewTab[] }) {
    const [activeKey, setActiveKey] = useState(tabs[0]?.key ?? "");
    const active = useMemo(
        () => tabs.find((t) => t.key === activeKey) ?? tabs[0],
        [tabs, activeKey]
    );

    if (!active) return null;

    return (
        <section id="podglad" className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
            <SectionHeading
                eyebrow="Jak to działa"
                title="Zobacz w praktyce"
                subtitle="Przełącz zakładki i zobacz przykładową rozmowę z botem."
            />

            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-4 sm:p-6">
                <div className="flex flex-wrap gap-2">
                    {tabs.map((t) => {
                        const isActive = t.key === active.key;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setActiveKey(t.key)}
                                className={[
                                    "rounded-2xl px-4 py-2 text-sm transition border",
                                    isActive
                                        ? "bg-white text-black border-white/20"
                                        : "bg-black/20 text-white/70 border-white/10 hover:bg-white/10",
                                ].join(" ")}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-6 grid gap-6 lg:grid-cols-2 lg:items-center">
                    <div>
                        <div className="text-sm font-semibold text-white">{active.title}</div>
                        <p className="mt-2 text-sm text-white/70">{active.desc}</p>

                        <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white">
                            <span className="text-white/50">Komenda:</span>{" "}
                            <span className="font-medium">{active.command}</span>
                        </div>
                    </div>

                    <div className="rounded-3xl border border-white/10 bg-black/20 p-4">
                        <div className="text-xs text-white/50">Podgląd czatu</div>
                        <div className="mt-3 space-y-2">
                            {active.transcript.map((m, idx) => {
                                const isBot = m.from === "bot";
                                return (
                                    <div
                                        key={idx}
                                        className={[
                                            "rounded-2xl px-4 py-3 text-sm border",
                                            isBot
                                                ? "border-white/10 bg-white/5 text-white"
                                                : "border-white/10 bg-black/30 text-white/80",
                                        ].join(" ")}
                                    >
                                        <div className="mb-1 text-xs text-white/50">
                                            {isBot ? "Bot" : "Użytkownik"}
                                        </div>
                                        {m.text}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
